import React, { useRef, useState, useCallback } from 'react';
import { ChevronsLeftRight } from 'lucide-react';

// Drag-to-reveal before/after comparison slider (pointer + touch).
export const BeforeAfterSlider = ({ before, after, beforePos, afterPos, className = '' }) => {
  const containerRef = useRef(null);
  const draggingRef = useRef(false);
  const [pos, setPos] = useState(50);
  const [active, setActive] = useState(false);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let pct = ((clientX - rect.left) / rect.width) * 100;
    pct = Math.max(0, Math.min(100, pct));
    setPos(pct);
  }, []);

  const onPointerDown = (e) => {
    draggingRef.current = true;
    setActive(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    setFromClientX(e.clientX);
  };
  const stop = () => { draggingRef.current = false; };

  return (
    <div
      ref={containerRef}
      className={`relative w-full aspect-[3/4] overflow-hidden rounded-xl border border-white/10 select-none touch-none cursor-ew-resize ${className}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stop}
      onPointerLeave={stop}
      onPointerCancel={stop}
      data-testid="before-after-slider"
    >
      {/* After (base layer) */}
      <img
        src={after}
        alt="After"
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover img-grade pointer-events-none"
        style={{ objectPosition: afterPos || '50% 25%' }}
      />
      {/* Before (clipped from the left) */}
      <img
        src={before}
        alt="Before"
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover img-grade pointer-events-none"
        style={{ objectPosition: beforePos || '50% 25%', clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      />

      {/* Corner labels */}
      <span className="absolute top-2.5 left-2.5 z-20 text-[10px] uppercase tracking-[0.25em] text-white/90 bg-black/50 backdrop-blur-sm border border-white/15 rounded-full px-2.5 py-1 pointer-events-none">
        Before
      </span>
      <span className="absolute top-2.5 right-2.5 z-20 text-[10px] uppercase tracking-[0.25em] text-white/90 bg-black/50 backdrop-blur-sm border border-white/15 rounded-full px-2.5 py-1 pointer-events-none">
        After
      </span>

      {/* Divider + handle */}
      <div
        className="absolute top-0 bottom-0 z-20 w-0.5 bg-white/90 shadow-[0_0_12px_rgba(0,0,0,0.6)] pointer-events-none"
        style={{ left: `${pos}%`, transform: 'translateX(-50%)' }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white text-black flex items-center justify-center shadow-lg">
          <ChevronsLeftRight className="w-5 h-5" strokeWidth={2} />
        </div>
      </div>

      {/* Drag hint — fades once the user interacts */}
      {!active && (
        <span className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 text-[10px] uppercase tracking-[0.2em] text-white/80 bg-black/55 backdrop-blur-sm border border-white/15 rounded-full px-3 py-1 pointer-events-none">
          Drag to compare
        </span>
      )}
    </div>
  );
};

export default BeforeAfterSlider;
